import { JButton } from '@atoms/JButton';
import JNumberField from '@atoms/JNumberField';
import { Popover, Stack } from '@mui/material';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { StackStyled, TypographyStyled } from './array-filter.styled';

interface ArrayFilterProps {
  onChangeMin: (_: number) => void;
  onChangeMax: (_: number) => void;

  labelFilter: string;
  labelInputEmpty: string;
  symbol?: string;
}

const ArrayFilter = ({
  onChangeMin,
  onChangeMax,
  labelFilter,
  labelInputEmpty,
  symbol = '',
}: ArrayFilterProps) => {
  const { t } = useTranslation();
  const [firstNumber, setFirstNumber] = useState<number | null>(null);
  const [secondNumber, setSecondNumber] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);

  const labelInput = () => {
    if (!isFiltering) {
      return labelInputEmpty;
    }
    if (firstNumber && secondNumber) {
      return `${symbol}${t('MIN')} ${firstNumber} - ${symbol}${t('MAX')}`;
    }
    if (firstNumber) {
      return `${symbol}${t('MIN')} ${firstNumber}`;
    }
    if (secondNumber) {
      return `${symbol}${t('MAX')} ${firstNumber}`;
    }
    return '';
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onFilter = () => {
    if (firstNumber !== null) {
      onChangeMin(firstNumber);
    }
    if (secondNumber !== null) {
      onChangeMax(secondNumber);
    }
    setIsFiltering(true);
    onClose();
  };

  const isValid = () => {
    if (firstNumber !== null && secondNumber === null) {
      return true;
    }
    if (secondNumber !== null && firstNumber === null) {
      return true;
    }
    if (
      secondNumber !== null &&
      firstNumber !== null &&
      firstNumber <= secondNumber
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <StackStyled
        onClick={(event) => setAnchorEl(event.currentTarget)}
        justifyContent={'center'}
        attrs={{ selected: isFiltering }}
        data-qa={`popover-button-${labelInputEmpty}`}
      >
        {labelInput()}
      </StackStyled>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={onClose}
        data-qa={`popover-filter-${labelInputEmpty}`}
      >
        <Stack p={2} alignItems={'center'} gap={4}>
          <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
            <JNumberField
              onNumberChange={(value) => setFirstNumber(value)}
              value={firstNumber}
              label={t('MIN')}
              hideButtons
              smallSize
            />
            <JNumberField
              onNumberChange={(value) => setSecondNumber(value)}
              value={secondNumber}
              label={t('MAX')}
              hideButtons
              smallSize
            />
          </Stack>
          <TypographyStyled>{labelFilter}</TypographyStyled>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            width={'100%'}
          >
            <JButton onClick={onClose}>{t('CANCEL')}</JButton>
            <JButton
              onClick={onFilter}
              disabled={!isValid()}
              color={'primary'}
              variant="contained"
            >
              {t('SAVE')}
            </JButton>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default ArrayFilter;
